import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ArcData } from '@swyftx/features/loans/components/LoanHealth/LoanGuageGraphic/gaugeUtils';
import { HealthConfig, HealthStatus } from '@swyftx/features/loans/components/LoanHealth/api/types/loanHealth.types';

import { BASE_GAUGE_DATA, BASE_HEALTH_PERCENTAGES, HEALTH_STATUS_CONFIG } from '../data/loanHealth.data';

const getHealthConfig = (percentage: number): HealthConfig => {
  if (percentage <= BASE_HEALTH_PERCENTAGES[HealthStatus.Inactive]) return HEALTH_STATUS_CONFIG[HealthStatus.Inactive];
  if (percentage < BASE_HEALTH_PERCENTAGES[HealthStatus.Attention]) return HEALTH_STATUS_CONFIG[HealthStatus.Healthy];
  if (percentage < BASE_HEALTH_PERCENTAGES[HealthStatus.Repayment]) return HEALTH_STATUS_CONFIG[HealthStatus.Attention];
  if (percentage < BASE_HEALTH_PERCENTAGES[HealthStatus.Default]) return HEALTH_STATUS_CONFIG[HealthStatus.Repayment];
  return HEALTH_STATUS_CONFIG[HealthStatus.Default];
};

const transformHealthPercentage = (percentage: number): number => {
  // Health percentage on the chart is not linear, so we need to map it to a non-linear scale
  const points = [
    { x: 45, y: 45 },
    { x: 50, y: 60 },
    { x: 55, y: 75 },
    { x: 60, y: 90 },
    { x: 100, y: 100 },
  ];

  if (percentage <= points[0].x || percentage >= points[points.length - 1].x) {
    return percentage;
  }

  const segmentIndex = points.findIndex((p) => percentage <= p.x);
  const p1 = points[segmentIndex - 1];
  const p2 = points[segmentIndex];

  const ratio = (percentage - p1.x) / (p2.x - p1.x);
  return p1.y + ratio * (p2.y - p1.y);
};

const calculateChartData = (percentage: number, activeColor: string): ArcData[] => {
  const inactiveColor = 'var(--color-background-surface-active)';

  const total = BASE_GAUGE_DATA.reduce((sum, item) => sum + item.value, 0);
  const targetValue = (percentage / 100) * total;
  const result: ArcData[] = [];
  let accumulated = 0;

  for (const { value } of BASE_GAUGE_DATA) {
    const nextAccumulated = accumulated + value;
    if (targetValue < accumulated) {
      result.push({ value, color: inactiveColor });
    } else if (targetValue < nextAccumulated) {
      const highlighted = targetValue - accumulated;
      result.push({ value: highlighted, color: activeColor });
      if (value - highlighted > 0) {
        result.push({ value: value - highlighted, color: inactiveColor });
      }
    } else {
      result.push({ value, color: activeColor });
    }
    accumulated = nextAccumulated;
  }
  return result;
};

export const useLoanHealthGauge = (percentage: number) => {
  const { t } = useTranslation('loans');

  const healthConfig = useMemo(() => getHealthConfig(percentage), [percentage]);
  const adjustedHealthPercentage = useMemo(() => transformHealthPercentage(percentage), [percentage]);

  return {
    healthStatus: healthConfig.status,
    statusTitle: t(healthConfig.statusTitle as unknown as TemplateStringsArray),
    pathFillColor: healthConfig.pathFillColor,
    textColor: healthConfig.textColor,
    chipColor: healthConfig.chipColor,
    chipText: t(healthConfig.chipText as unknown as TemplateStringsArray),
    adjustedHealthPercentage,
    calculateChartData,
  };
};
